import { useState } from 'react';
import {
  Paper,
  Group,
  Text,
  Button,
  Divider,
  Stack,
  NumberInput,
  Select,
  Textarea,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import {
  faFileInvoiceDollar,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { surfaceHeaderProps } from '../SurfaceHeader';

const formatUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    amount
  );

interface AmountRowProps {
  readonly label: string;
  readonly amount: number;
  readonly bold?: boolean;
  readonly dimmed?: boolean;
}

function AmountRow({ label, amount, bold, dimmed }: AmountRowProps) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text fz="sm" fw={bold ? 600 : 400} c={dimmed ? 'dimmed' : undefined}>
        {label}
      </Text>
      <Text fz="sm" fw={bold ? 600 : 400} c={dimmed ? 'dimmed' : undefined}>
        {formatUSD(amount)}
      </Text>
    </Group>
  );
}

export interface PaymentFormData {
  readonly payAmount: number;
  readonly paymentType: string | null;
  readonly processor: string | null;
  readonly comments: string;
}

export interface ApplyPaymentProps {
  readonly policyNumber: string;
  readonly billingType?: string;
  readonly totalPremium?: number;
  readonly commissions?: number;
  readonly invoiceAmount?: number;
  readonly alreadyPaid?: number;
  readonly amountDue?: number;
  readonly paymentTypes?: string;
  readonly processors?: string;
  readonly onSubmit?: (data: PaymentFormData) => void;
  readonly onCancel?: () => void;
}

export function ApplyPayment({
  policyNumber,
  billingType,
  totalPremium = 0,
  commissions = 0,
  invoiceAmount = 0,
  alreadyPaid = 0,
  amountDue = 0,
  paymentTypes,
  processors,
  onSubmit,
  onCancel,
}: ApplyPaymentProps) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [payAmount, setPayAmount] = useState<number | string>('');
  const [paymentType, setPaymentType] = useState<string | null>(null);
  const [processor, setProcessor] = useState<string | null>(null);
  const [comments, setComments] = useState('');

  const paymentTypeList = paymentTypes
    ? paymentTypes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : ['Check', 'Wire Transfer', 'ACH', 'Credit Card'];

  const processorList = processors
    ? processors
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : ['Stripe', 'Braintree', 'Authorize.net'];

  const parsedPay =
    typeof payAmount === 'string' ? Number.parseFloat(payAmount) : payAmount;
  const balance = Number.isFinite(parsedPay)
    ? amountDue - parsedPay
    : amountDue;

  return (
    <Paper
      radius="md"
      withBorder
      p="lg"
      style={{ borderColor: 'var(--mantine-color-blue-2)', width: '100%' }}
    >
      {/* Header */}
      <Group justify="space-between" {...surfaceHeaderProps('lg')}>
        <Group gap="xs">
          <FontAwesomeIcon
            icon={faFileInvoiceDollar}
            style={{
              color: 'var(--mantine-color-blue-6)',
              width: 18,
              height: 18,
            }}
          />
          <Text fw={700} fz={13} c="blue.6" style={{ letterSpacing: 0.5 }}>
            APPLY PAYMENT
          </Text>
        </Group>
        {amountDue > 0 && (
          <Group gap="xs">
            <Button variant="outline" color="gray" size="xs" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="outline"
              color="blue"
              size="xs"
              onClick={() =>
                onSubmit?.({
                  payAmount: Number.isFinite(parsedPay) ? parsedPay : 0,
                  paymentType,
                  processor,
                  comments,
                })
              }
            >
              Make Payment
            </Button>
          </Group>
        )}
      </Group>

      <Stack gap="xs">
        {/* Policy metadata */}
        <Group gap={0} wrap="nowrap">
          <Text fz="sm" c="dimmed" style={{ minWidth: 72, flexShrink: 0 }}>
            Policy #
          </Text>
          <Text fz="sm" fw={500}>
            {policyNumber}
          </Text>
        </Group>
        {billingType && (
          <Group gap={0} wrap="nowrap">
            <Text fz="sm" c="dimmed" style={{ minWidth: 72, flexShrink: 0 }}>
              Billing
            </Text>
            <Text fz="sm">{billingType}</Text>
          </Group>
        )}

        <Divider />

        {/* Expandable breakdown */}
        <UnstyledButton onClick={() => setBreakdownOpen((o) => !o)}>
          <Group justify="space-between">
            <Text fz="sm" c="blue.6" fw={500}>
              Breakdown
            </Text>
            <FontAwesomeIcon
              icon={breakdownOpen ? faChevronUp : faChevronDown}
              style={{
                color: 'var(--mantine-color-blue-6)',
                width: 12,
                height: 12,
              }}
            />
          </Group>
        </UnstyledButton>
        <Collapse in={breakdownOpen}>
          <Stack gap={4} pl="xs" pb={4}>
            <AmountRow label="Total Premium" amount={totalPremium} dimmed />
            <AmountRow label="Commissions" amount={commissions} dimmed />
          </Stack>
        </Collapse>

        <Divider />

        {/* Invoice summary */}
        <AmountRow label="Invoice Amount" amount={invoiceAmount} />
        <AmountRow label="Already Paid" amount={alreadyPaid} />

        <Divider />

        <AmountRow label="Amount Due" amount={amountDue} bold />

        {amountDue > 0 && (
          <>
            <NumberInput
              label="Pay"
              prefix="$"
              decimalScale={2}
              fixedDecimalScale
              min={0}
              value={payAmount}
              onChange={setPayAmount}
              size="sm"
            />

            <Group justify="space-between" wrap="nowrap">
              <Text fz="sm" c="dimmed">
                Balance
              </Text>
              <Text fz="sm" fw={500}>
                {formatUSD(balance)}
              </Text>
            </Group>

            <Divider />

            <Select
              label="Payment Type"
              placeholder="Select a payment type..."
              data={paymentTypeList}
              value={paymentType}
              onChange={setPaymentType}
              size="sm"
            />
            <Select
              label="Processor"
              placeholder="Select a processor..."
              data={processorList}
              value={processor}
              onChange={setProcessor}
              size="sm"
            />
            <Textarea
              label="Comments"
              placeholder="Details of payment"
              value={comments}
              onChange={(e) => setComments(e.currentTarget.value)}
              minRows={3}
              size="sm"
            />
          </>
        )}
      </Stack>
    </Paper>
  );
}
